import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { Star, Play, Pause, X, Music2 } from "lucide-react";

const STORAGE_KEY = "lechatceltique.fav_tracks";
const MAX_FAVS = 3;

const FavoritesContext = createContext(null);

/* ============================================================
   Provider — stores up to 3 pinned tracks in localStorage,
   exposes helpers (toggle, isFav), and renders the global
   mini-player at the bottom of every page.
   ============================================================ */
export function FavoritesProvider({ children }) {
  const [favs, setFavs] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setFavs(JSON.parse(raw).slice(0, MAX_FAVS));
    } catch { /* ignore */ }
  }, []);

  const persist = (next) => {
    setFavs(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  const isFav = useCallback((id) => favs.some((t) => t.id === id), [favs]);

  const toggleFav = useCallback((track) => {
    if (!track || !track.id) return;
    setFavs((cur) => {
      const existing = cur.find((t) => t.id === track.id);
      let next;
      if (existing) {
        next = cur.filter((t) => t.id !== track.id);
      } else {
        next = [...cur, track].slice(-MAX_FAVS);
      }
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const removeFav = (id) => {
    persist(favs.filter((t) => t.id !== id));
    if (playingId === id) stopPlayback();
  };

  const stopPlayback = () => {
    try { audioRef.current?.pause(); } catch { /* ignore */ }
    setPlayingId(null);
  };

  const playFav = (track) => {
    if (!audioRef.current || !track) return;
    if (playingId === track.id) { stopPlayback(); return; }
    audioRef.current.src = track.url;
    audioRef.current.volume = 0.3;
    audioRef.current.loop = true;
    audioRef.current.load();
    audioRef.current.play().then(() => setPlayingId(track.id)).catch(() => setPlayingId(null));
  };

  const value = { favs, isFav, toggleFav, removeFav, playFav, playingId, stopPlayback };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
      <audio ref={audioRef} preload="none" crossOrigin="anonymous" />
      <FavoritesMiniPlayer />
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  return useContext(FavoritesContext) || {
    favs: [], isFav: () => false, toggleFav: () => {}, removeFav: () => {}, playFav: () => {}, playingId: null, stopPlayback: () => {},
  };
}

/* ============================================================
   Mini-player — fixed bottom-right, collapsible.
   Shows up to 3 favorite tracks with play/stop + unpin.
   Hidden entirely when there are 0 favs (keeps home clean).
   ============================================================ */
function FavoritesMiniPlayer() {
  const { favs, removeFav, playFav, playingId, stopPlayback } = useFavorites();
  const [open, setOpen] = useState(true);

  if (favs.length === 0) return null;

  return (
    <div
      data-testid="fav-mini-player"
      style={{
        position: "fixed",
        bottom: 18,
        right: 18,
        zIndex: 9999,
        maxWidth: open ? 320 : 56,
        transition: "max-width 240ms ease",
      }}
    >
      <div
        style={{
          background: "linear-gradient(180deg, rgba(14,10,24,0.96), rgba(6,4,14,0.98))",
          border: "1px solid rgba(245,213,114,0.35)",
          borderRadius: 20,
          boxShadow: "0 18px 42px -14px rgba(0,0,0,0.75), 0 0 0 1px rgba(245,213,114,0.08)",
          overflow: "hidden",
          backdropFilter: "blur(12px)",
        }}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="fav-mini-toggle"
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            padding: "10px 14px",
            background: "transparent",
            color: "#f5d572",
            fontSize: 11,
            fontFamily: '"IBM Plex Mono", monospace',
            textTransform: "uppercase",
            letterSpacing: "0.2em",
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Music2 size={13} /> {open ? `Mes favoris · ${favs.length}/${MAX_FAVS}` : "★"}
          </span>
          {open && <span style={{ color: "#8c8597" }}>▾</span>}
        </button>
        {open && (
          <div style={{ padding: "4px 8px 10px" }}>
            {favs.map((t) => {
              const isPlaying = playingId === t.id;
              return (
                <div
                  key={t.id}
                  data-testid={`fav-mini-item-${t.id}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "7px 8px",
                    borderRadius: 10,
                    marginBottom: 4,
                    background: isPlaying ? "rgba(245,213,114,0.12)" : "transparent",
                    border: isPlaying ? "1px solid rgba(245,213,114,0.5)" : "1px solid transparent",
                    transition: "background 180ms ease, border-color 180ms ease",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => playFav(t)}
                    data-testid={`fav-mini-play-${t.id}`}
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: isPlaying ? "#f5d572" : "rgba(245,213,114,0.18)",
                      color: isPlaying ? "#0a0612" : "#f5d572",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {isPlaying ? <Pause size={13} /> : <Play size={13} />}
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#e8dfc3",
                        fontFamily: '"IBM Plex Mono", monospace',
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {t.label}
                    </div>
                    {t.persona && (
                      <div
                        style={{
                          fontSize: 9,
                          color: "#8c8597",
                          textTransform: "uppercase",
                          letterSpacing: "0.14em",
                          marginTop: 2,
                        }}
                      >
                        {t.persona}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFav(t.id)}
                    data-testid={`fav-mini-remove-${t.id}`}
                    aria-label="unpin"
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: "transparent",
                      color: "#8c8597",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <X size={12} />
                  </button>
                </div>
              );
            })}
            {playingId && (
              <button
                type="button"
                onClick={stopPlayback}
                data-testid="fav-mini-stop-all"
                style={{
                  width: "100%",
                  marginTop: 4,
                  padding: "6px 10px",
                  borderRadius: 10,
                  background: "rgba(245,213,114,0.08)",
                  color: "#f5d572",
                  fontSize: 10,
                  fontFamily: '"IBM Plex Mono", monospace',
                  textTransform: "uppercase",
                  letterSpacing: "0.18em",
                }}
              >
                ■ Stop
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   PinStar — standalone star button to pin/unpin a track.
   Rendered inside MusicPicker options.
   ============================================================ */
export function PinStar({ track, size = 13 }) {
  const { isFav, toggleFav, favs } = useFavorites();
  const pinned = isFav(track?.id);
  const atLimit = favs.length >= MAX_FAVS && !pinned;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); if (!atLimit) toggleFav(track); }}
      data-testid={`fav-pin-${track?.id}`}
      aria-label={pinned ? "unpin" : "pin"}
      title={atLimit ? "Maximum 3 favoris — retire-en un d'abord" : (pinned ? "Retirer des favoris" : "Épingler")}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 22,
        height: 22,
        borderRadius: "50%",
        background: pinned ? "rgba(245,213,114,0.22)" : "transparent",
        color: pinned ? "#f5d572" : (atLimit ? "rgba(140,133,151,0.5)" : "#8c8597"),
        cursor: atLimit ? "not-allowed" : "pointer",
        flexShrink: 0,
      }}
    >
      <Star size={size} fill={pinned ? "currentColor" : "none"} />
    </button>
  );
}
